import React, { useState, useEffect, useMemo } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import RTMPPanel from '../rtmp/RTMPPanel';
import {
  PanelCard, PanelFooter, Segmented, TextField, NumberField, Choice, Toggle, InfoRow, Notice,
  useReportDirty,
} from './controls';

// Only the masks worth offering for a camera subnet.
const PREFIX_OPTIONS = [
  { prefix: 30, mask: '255.255.255.252' },
  { prefix: 29, mask: '255.255.255.248' },
  { prefix: 28, mask: '255.255.255.240' },
  { prefix: 27, mask: '255.255.255.224' },
  { prefix: 26, mask: '255.255.255.192' },
  { prefix: 25, mask: '255.255.255.128' },
  { prefix: 24, mask: '255.255.255.0' },
  { prefix: 23, mask: '255.255.254.0' },
  { prefix: 22, mask: '255.255.252.0' },
  { prefix: 16, mask: '255.255.0.0' },
  { prefix: 8, mask: '255.0.0.0' },
];

const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

/**
 * Validate before the camera does. A typo'd address here costs a reboot and, on a static
 * network, a site visit to get the camera back — so a bad octet must fail in the form, not
 * halfway through an ONVIF SetNetworkInterfaces.
 */
function validate(vals: any) {
  const errors: Record<string, string> = {};
  if (!vals.dhcp) {
    if (!vals.ip) errors.ip = 'Required for a static address';
    else if (!IPV4.test(vals.ip)) errors.ip = 'Not a valid IPv4 address';
    if (vals.gateway && !IPV4.test(vals.gateway)) errors.gateway = 'Not a valid IPv4 address';
    if (vals.dns1 && !IPV4.test(vals.dns1)) errors.dns1 = 'Not a valid IPv4 address';
    if (vals.dns2 && !IPV4.test(vals.dns2)) errors.dns2 = 'Not a valid IPv4 address';
    // A gateway outside the camera's own subnet is unreachable, which is the most common
    // way a static reconfiguration silently strands a camera.
    if (!errors.ip && !errors.gateway && vals.ip && vals.gateway && sameSubnet(vals.ip, vals.gateway, vals.prefix_length) === false) {
      errors.gateway = `Outside the camera's /${vals.prefix_length} subnet`;
    }
  }
  const port = Number(vals.rtsp_port);
  if (!port || port < 1 || port > 65535) errors.rtsp_port = 'Must be between 1 and 65535';
  return errors;
}

function toInt(ip: string): number | null {
  if (!IPV4.test(ip)) return null;
  return ip.split('.').reduce((acc, o) => (acc << 8) + Number(o), 0) >>> 0;
}

function sameSubnet(a: string, b: string, prefix: number): boolean | null {
  const ia = toInt(a);
  const ib = toInt(b);
  if (ia == null || ib == null || !prefix) return null;
  const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
  return (ia & mask) === (ib & mask);
}

// `nodeId` is only meaningful on the cloud frontend, where RTMP config is proxied per node;
// the local build ignores it. Kept in the signature so both copies stay identical.
export default function NetworkPanel({ nodeId, cameraId, config, onSave, loading, onDirtyChange }: any) {
  const blank = {
    dhcp: false, ip: '', prefix_length: 24, gateway: '', dns1: '', dns2: '',
    rtsp_port: 554, username: '', password: '', encryption_enabled: false,
  };
  const [vals, setVals] = useState<any>(blank);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const baseline = useMemo(() => (config ? {
    dhcp: !!config.dhcp,
    ip: config.ip || '',
    prefix_length: config.prefix_length || 24,
    gateway: config.gateway || '',
    dns1: config.dns?.[0] || '',
    dns2: config.dns?.[1] || '',
    rtsp_port: config.rtsp_port || 554,
    username: config.username || '',
    password: '',
    encryption_enabled: config.encryption_enabled || false,
  } : blank), [config]);

  const reset = () => { setVals(baseline); setTouched({}); };
  useEffect(reset, [baseline]);

  const errors = validate(vals);
  const dirty = JSON.stringify(vals) !== JSON.stringify(baseline);
  useReportDirty(dirty, onDirtyChange);

  // Anything that moves the camera's address or stream port breaks existing connections.
  const disruptive =
    vals.dhcp !== baseline.dhcp ||
    (!vals.dhcp && !!baseline.ip && vals.ip !== baseline.ip) ||
    Number(vals.rtsp_port) !== Number(baseline.rtsp_port);

  const update = (k: string, v: any) => {
    setVals((prev: any) => ({ ...prev, [k]: v }));
    setTouched((prev) => ({ ...prev, [k]: true }));
  };

  // Only surface a field's error once the user has been in it — a freshly loaded camera with
  // no gateway shouldn't open covered in red.
  const errorFor = (k: string) => (touched[k] ? errors[k] : undefined);

  const submit = () => {
    setTouched(Object.keys(vals).reduce((acc: any, k) => ({ ...acc, [k]: true }), {}));
    if (Object.keys(errors).length) return;
    const payload: any = {
      dhcp: vals.dhcp,
      rtsp_port: Number(vals.rtsp_port) || undefined,
      username: vals.username || undefined,
      password: vals.password || undefined,
      encryption_enabled: vals.encryption_enabled,
    };
    if (!vals.dhcp) {
      payload.ip = vals.ip || undefined;
      payload.prefix_length = Number(vals.prefix_length) || undefined;
      payload.gateway = vals.gateway || undefined;
      payload.dns = [vals.dns1, vals.dns2].filter(Boolean);
    }
    onSave(payload);
  };

  const invalid = Object.keys(errors).length > 0;

  return (
    <div className="space-y-6">
      <PanelCard
        title="Network Configuration"
        description="Addressing and credentials the recorder uses to reach this camera."
        footer={
          <PanelFooter
            dirty={dirty} loading={loading} onReset={reset} onSave={submit} disabled={invalid}
            dirtyLabel={invalid && Object.keys(touched).length
              ? 'Fix the highlighted fields to continue'
              : disruptive ? 'Unsaved changes — the camera will reconnect' : 'Unsaved changes'}
          />
        }
      >
        {disruptive && (
          <Notice tone="warn" icon={ExclamationTriangleIcon}>
            Changing the address or RTSP port disconnects the camera while it reboots its network
            stack. The stored stream URL is updated to the address the camera reports back, but a
            DHCP lease can move again later — a static reservation is safer for recorded cameras.
          </Notice>
        )}

        <div className="pt-1">
          <Segmented
            label="IP Assignment" value={vals.dhcp} disabled={loading}
            onChange={(v: boolean) => update('dhcp', v)}
            options={[{ value: false, label: 'Static' }, { value: true, label: 'DHCP' }]}
          />
        </div>

        {vals.dhcp ? (
          <div className="mt-3 rounded-lg border border-border bg-muted px-4 py-3">
            <p className="mb-2.5 text-[12px] text-muted-foreground">
              The camera takes its address from the DHCP server.
            </p>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <InfoRow label="Current Address" mono
                value={config?.ip ? `${config.ip}${config.prefix_length ? `/${config.prefix_length}` : ''}` : null} />
              <InfoRow label="Gateway" mono value={config?.gateway} />
              <InfoRow label="DNS" mono value={config?.dns?.filter(Boolean).join(', ')} />
            </div>
          </div>
        ) : (
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField label="IP Address" mono value={vals.ip} placeholder="192.168.1.64"
              onChange={(v: string) => update('ip', v.trim())} disabled={loading} error={errorFor('ip')} />
            <Choice label="Subnet Mask" value={vals.prefix_length} disabled={loading}
              options={PREFIX_OPTIONS.map((o) => o.prefix)}
              labels={PREFIX_OPTIONS.map((o) => `${o.mask} (/${o.prefix})`)}
              onChange={(v: string) => update('prefix_length', Number(v))} />
            <TextField label="Default Gateway" mono value={vals.gateway} placeholder="192.168.1.1"
              onChange={(v: string) => update('gateway', v.trim())} disabled={loading} error={errorFor('gateway')} />
            <TextField label="Primary DNS" mono value={vals.dns1} placeholder="8.8.8.8"
              onChange={(v: string) => update('dns1', v.trim())} disabled={loading} error={errorFor('dns1')} />
            <TextField label="Secondary DNS" mono value={vals.dns2} placeholder="Optional"
              onChange={(v: string) => update('dns2', v.trim())} disabled={loading} error={errorFor('dns2')} />
          </div>
        )}

        <div className="mt-5 border-t border-border pt-4">
          <p className="mb-3 text-[13px] font-semibold text-foreground">Ports & Credentials</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <NumberField label="RTSP Port" value={vals.rtsp_port}
              onChange={(v: number) => update('rtsp_port', v)} disabled={loading} error={errorFor('rtsp_port')} />
            <InfoRow label="ONVIF Port" mono value={config?.onvif_port} />
            <TextField label="Username" value={vals.username}
              onChange={(v: string) => update('username', v)} disabled={loading} />
            <TextField label="Password" type="password" value={vals.password}
              placeholder="Leave empty to keep current"
              onChange={(v: string) => update('password', v)} disabled={loading} />
          </div>
        </div>

        {(config?.hostname || config?.mac_address) && (
          <div className="mt-4 grid grid-cols-2 gap-4 border-t border-border pt-4">
            <InfoRow label="Hostname" mono value={config.hostname} />
            <InfoRow label="MAC Address" mono value={config.mac_address} />
          </div>
        )}

        <div className="mt-4 border-t border-border pt-4">
          <Toggle
            label="Enable encryption" description="Use the camera's encrypted transport where supported."
            checked={vals.encryption_enabled} disabled={loading}
            onChange={(v: boolean) => update('encryption_enabled', v)}
          />
        </div>
      </PanelCard>

      {/* Streaming out to an external RTMP target is its own concern — it was previously buried
          at the bottom of the network form, where it read like part of the camera's addressing. */}
      <RTMPPanel nodeId={nodeId} cameraId={cameraId} />
    </div>
  );
}
